import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useConfirm } from 'material-ui-confirm';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useElementSize } from 'usehooks-ts';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Map from 'ol/Map';
import { transform } from "ol/proj";
import Typography from "@mui/material/Typography";
import SvgIcon from "@mui/material/SvgIcon";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { VEHICLE_KINDS } from '../../../config/const';
import { store, RootState } from '../../../app/store';
import { hourOrNbDays, hour, toDate } from '../../../utils/TimeUtils';
import { trackingActions } from '../../../redux/tracking/trackingSlice';
import { geomarkersActions } from "../../../redux/geomarkers/geomarkersSlice";
import { Accordion, AccordionSummary, AccordionDetails } from '../../utils/accordion/Accordion';
import { COLOR_TRACKING_FUNCTIONS, COLOR_TRACK_NONE } from '../../../config/tracking';
import { widgetActions } from "../../../redux/widget/widgetSlice";
import findLastIndex from 'lodash/findLastIndex';
import { NAVIGATION_GRID_ID, ATTITUDE_WIDGET_ID } from "../../../config/widget";
import { ColorPalette } from "../../telemetry/timeSeriesTab/Side";
import { COLORS_RESTRICTEDSET } from "../../../utils/StringUtils";

export const lastSelector = createSelector(
    (state: RootState) => state.telemetry.streams,
    (state: RootState) => state.replay.currentTime,
    (_: RootState, properties: { id: string, source: string | null }) => properties,
    (streams, currentTime, { id, source }) => {
        if (source !== null) {
            const currentTS = toDate(currentTime).getTime() / 1000;
            if (id in streams && source in streams[id]) {
                const lastIndex = currentTS ? findLastIndex(streams[id][source][0], (ts: number) => ts <= currentTS) : streams[id][source][0].length - 1;
                if (lastIndex >= 0) {
                    return ({
                        timestamp: streams[id][source][0][lastIndex],
                        position: streams[id][source][1][lastIndex],
                        displayDate: currentTS ? hour(streams[id][source][0][lastIndex]) : hourOrNbDays(streams[id][source][0][lastIndex])
                    });
                } else return null;
            };
        };
        return null;
    }
);

function Actions({ tracker, trackerId, lastPosition, map }: { tracker: any, trackerId: string, lastPosition: any, map: Map }) {

    const dispatch = useDispatch();
    const confirm = useConfirm();
    const medium = useMediaQuery((theme: any) => theme.breakpoints.down('xl'));
    const isActive = useSelector((state: RootState) => state.replay.isActive);
    const [clickTimeout, setClickTimeout] = useState<any>(null);

    const toggleHud = useCallback(() => {
        if (tracker.shown) {
            tracker.hud.shown
                ? dispatch(trackingActions.hide_hud({ plid: trackerId }))
                : dispatch(trackingActions.show_hud({ plid: trackerId }));
        };
    }, [tracker.shown, tracker.hud, trackerId, dispatch]);

    const toggleCircles = useCallback(() => {
        if (tracker.shown) {
            tracker.circles
                ? dispatch(trackingActions.hide_cirlces({ plid: trackerId }))
                : dispatch(trackingActions.show_circles({ plid: trackerId }));
        };
    }, [tracker.shown, tracker.circles, trackerId, dispatch]);

    const centerMap = useCallback((e: any) => {
        if (tracker.shown && lastPosition?.position) {
            map.getView().setCenter(transform(lastPosition.position, "EPSG:4326", store.getState().map.projection));
        };
    }, [tracker.shown, lastPosition, map]);

    const addWidget = useCallback(() => {
        dispatch(widgetActions.add_widget({ gridId: NAVIGATION_GRID_ID, id: `attitude-${trackerId}`, kind: ATTITUDE_WIDGET_ID, params: { trackerId }, medium }))
    }, [dispatch, medium, trackerId]);

    const dropPoint = useCallback(() => {
        confirm({
            confirmationButtonProps: { color: "inherit" },
            cancellationButtonProps: { color: "inherit" },
            description: `Please confirm drop of point at current ${tracker.name} postion`
        })
            .then(() => {
                dispatch(geomarkersActions.drop_point({
                    feature: {
                        geometry: { type: "Point", coordinates: lastPosition.position },
                        properties: { color: "#0000FF", group: "drop", label: `${tracker.name} Drop` },
                        type: "Feature"
                    }
                }))
            })
            .catch(() => { });
    }, [dispatch, confirm, lastPosition, tracker.name]);

    const handleSingleClick = (event: any) => {
        centerMap(event);
    };

    const handleDoubleClick = (event: any) => {
        dispatch(trackingActions.set_follow({ plid: trackerId, follow: !tracker.follow }))
    };

    const handleClick = (event: any) => {
        if (clickTimeout) {
            // Double click case
            clearTimeout(clickTimeout);
            setClickTimeout(null);
            handleDoubleClick(event);
        } else {
            // Single click case
            const timeout = setTimeout(() => {
                handleSingleClick(event);
                setClickTimeout(null);
            }, 300);
            setClickTimeout(timeout);
        }
    };

    return (
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Actions:
            </Typography>
            <Tooltip title="Drop geomarker" placement="top">
                <span>
                    <IconButton color="inherit" size="small" disabled={isActive} onClick={dropPoint}>
                        <LocationOnIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Center on aircraft" placement="top">
                <IconButton color={tracker.follow ? "secondary" : "inherit"} size="small" onClick={handleClick}>
                    <GpsFixedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title={`${tracker.circles ? "Hide" : "Show"} aircraft radius circles`} placement="top">
                <IconButton color="inherit" size="small" onClick={toggleCircles}>
                    <SvgIcon fontSize="small">
                        {tracker.circles ? (
                            <path d="m 12.099944,4.3464675 c -4.1199452,0 -7.4576031,3.3407866 -7.4599547,7.4607225 0.00235,4.123074 3.3400095,7.459157 7.4599547,7.462292 4.121514,-0.0033 7.459172,-3.339218 7.462306,-7.462292 -0.004,-4.1199359 -3.340792,-7.4607225 -7.462306,-7.4607225 z M 16.02079,15.728029 c -1.007253,1.005684 -2.385272,1.622578 -3.920846,1.625713 C 10.565938,17.350642 9.1879204,16.733713 8.1806658,15.728029 7.1734112,14.720776 6.5580844,13.344328 6.5565166,11.80719 6.5580866,10.27319 7.1734112,8.8951725 8.1806658,7.8863522 9.1887042,6.8822353 10.565938,6.2622069 12.099944,6.2622069 c 1.535574,0 2.913593,0.6200284 3.920846,1.6241453 1.007256,1.0088203 1.62415,2.3868378 1.625719,3.9208378 -7.84e-4,1.537138 -0.618463,2.913586 -1.625719,3.920839 z M 12.099944,0.30805117 C 5.6276475,0.30805117 0.38365246,5.5528189 0.38208474,12.025887 0.38365279,18.498954 5.6276475,23.742153 12.100728,23.745288 18.572241,23.742023 23.815453,18.498954 23.81702,12.025887 23.814669,5.5528189 18.572241,0.30805117 12.099944,0.30805117 Z M 18.992388,18.91518 c -1.774649,1.768374 -4.195196,2.854013 -6.892444,2.857148 C 9.4011292,21.769193 6.9797986,20.683554 5.2082847,18.91518 3.4414738,17.145237 2.3558335,14.724696 2.3534821,12.025887 2.3558335,9.328644 3.4414738,6.9065349 5.2082847,5.1334569 6.9797986,3.3682176 9.4003454,2.2794441 12.099944,2.2794441 c 2.697248,0 5.117795,1.0887735 6.892444,2.8540128 1.76681,1.773078 2.852451,4.1951871 2.854019,6.8924301 -0.0023,2.699593 -1.086424,5.11935 -2.854019,6.889293 z M 14.52441,12.025887 A 2.4236822,2.423677 0 0 1 12.100729,14.449563 2.4236822,2.423677 0 0 1 9.6770465,12.025887 2.4236822,2.423677 0 0 1 12.100729,9.6022086 2.4236822,2.423677 0 0 1 14.52441,12.025887 Z" />
                        ) : (
                            <path d="M 17.01514,0.78671636 4.1758599,21.586168 c -0.4473499,0.72436 -0.2224101,1.673451 0.5017402,2.120168 0.2521349,0.155582 0.5314651,0.23021 0.8078439,0.23021 0.5167081,0 1.0216106,-0.259513 1.3127463,-0.73195 L 19.63747,2.4049341 C 20.084609,1.6807837 19.859879,0.73169367 19.135519,0.28497622 18.411579,-0.16195206 17.461645,0.0625661 17.01514,0.78671636 Z M 11.980205,4.2413801 c -4.046348,0 -7.3243822,3.2811133 -7.3266917,7.3274609 0.00231,4.049428 3.2803437,7.325923 7.3266917,7.329002 4.047887,-0.0031 7.325921,-3.279574 7.329,-7.329002 -0.0038,-4.0463464 -3.281113,-7.3274609 -7.329,-7.3274609 z m 3.850804,11.1782649 c -0.98926,0.987723 -2.342663,1.593597 -3.850804,1.596676 C 10.473603,17.013242 9.1202005,16.407368 8.1309393,15.419645 7.1416782,14.430385 6.5373435,13.078523 6.5358039,11.568841 6.5373441,10.06224 7.1416782,8.7088374 8.1309393,7.7180364 9.1209704,6.731855 10.473603,6.1229013 11.980205,6.1229013 c 1.508141,0 2.861544,0.6089537 3.850804,1.5951351 0.989261,0.990801 1.595136,2.3442036 1.596676,3.8508046 -7.7e-4,1.509682 -0.607415,2.861544 -1.596676,3.850804 z M 11.980205,0.27509714 c -6.3566769,0 -11.50699372,5.15108776 -11.50853343,11.50853386 0.00154,6.357446 5.15185653,11.506992 11.50930343,11.510072 6.355906,-0.0031 11.505453,-5.152626 11.506992,-11.510072 C 23.485681,5.4261849 18.336881,0.27509714 11.980205,0.27509714 Z M 18.749522,18.549868 C 17.006575,20.286656 14.62927,21.352904 11.980205,21.355983 9.3296005,21.352904 6.9515245,20.286656 5.2116566,18.549868 3.4764079,16.811541 2.4101614,14.434235 2.4078519,11.783631 2.4101623,9.1345663 3.4764079,6.75572 5.2116566,5.0143132 c 1.7398679,-1.7337094 4.117174,-2.803035 6.7685484,-2.803035 2.649065,0 5.02637,1.0693256 6.769317,2.803035 1.735249,1.7414068 2.801496,4.1202531 2.803036,6.7693178 -0.0023,2.651374 -1.067017,5.02791 -2.803036,6.766237 z M 14.36136,11.783631 A 2.3803857,2.3803857 0 0 1 11.980975,14.164016 2.3803857,2.3803857 0 0 1 9.6005888,11.783631 2.3803857,2.3803857 0 0 1 11.980975,9.4032445 2.3803857,2.3803857 0 0 1 14.36136,11.783631 Z" />
                        )}
                    </SvgIcon>
                </IconButton>
            </Tooltip>
            <Tooltip title="Open attitude widget" placement="top">
                <IconButton color="inherit" size="small" onClick={addWidget}>
                    <SvgIcon viewBox="0 0 100 100" fontSize="small">
                        <path d="M50,10C27.909,10,10,27.909,10,50C10,72.091,27.909,90,50,90C72.091,90,90,72.091,90,50C90,27.909,72.091,10,50,10ZM60,70L60,75L40,75L40,70L60,70ZM45,65L45,60L55,60L55,65L45,65ZM45,80L55,80L55,85L45,85L45,80ZM55,50C55,52.761,52.761,55,50,55S45,52.761,45,50L15,50C15,44.521,16.268,39.335,18.522,34.713L22.769,37.165L24.019,35L25.269,32.835L21.027,30.386C23.526,26.707,26.706,23.526,30.386,21.027L32.835,25.269L35,24.019L37.165,22.769L34.713,18.522C37.952,16.943,41.47,15.855,45.171,15.341L50,25L54.829,15.341C58.529,15.854,62.048,16.942,65.287,18.522L62.835,22.769L65,24.019L67.165,25.269L69.614,21.027C73.293,23.526,76.474,26.706,78.973,30.386L74.731,32.835L75.981,35L77.231,37.165L81.478,34.713C83.732,39.335,85,44.521,85,50L55,50Z" stroke="none"></path><path d="M40,30L60,30L60,35L40,35L40,30Z" stroke="none"></path><path d="M55,40L45,40L45,45L50,45L55,45Z" stroke="none"></path><path d="M35,24.019L35,24.019" stroke="none"></path><path d="M24.019,35L24.019,35" stroke="none"></path><path d="M65,24.019L65,24.019" stroke="none"></path><path d="M75.981,35L75.981,35" stroke="none"></path>
                    </SvgIcon>
                </IconButton>
            </Tooltip>
            <Tooltip title={`${tracker.hud.shown ? "Hide" : "Show"} HUD`} placement="top">
                <Button sx={{ fontWeight: 'bold', color: 'white', textDecoration: tracker.hud.shown ? 'none' : 'line-through' }} onClick={toggleHud}>HUD</Button>
            </Tooltip>
        </Stack>
    );
};

function TrackLength({ tracker, trackerId }: { tracker: any, trackerId: string }) {

    const dispatch = useDispatch();

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(
            trackingActions.set_tracker_length({
                plid: trackerId,
                length: event.target.value
            })
        );
    };

    return (
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Track Length:
            </Typography>
            <TextField
                select
                value={tracker.length}
                onChange={onChange}
                variant="outlined"
                size="small"
                SelectProps={{
                    native: true,
                }}
            >
                <option value={43200}>Full</option>
                <option value={7200}>2h</option>
                <option value={3600}>1h</option>
                <option value={1800}>30m</option>
                <option value={900}>15m</option>
                <option value={30}>30s</option>
                <option value={1}>None</option>
            </TextField>
        </Stack>
    );
};

function VehicleColor({ tracker, trackerId }: { tracker: any, trackerId: string }) {

    const dispatch = useDispatch();

    const onChange = (color: any) => {
        dispatch(
            trackingActions.set_color({
                plid: trackerId,
                color
            })
        );
    };

    return (
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Vehicle Color:
            </Typography>
            <ColorPalette color={tracker.color} onChange={onChange} size="small" colors={[...COLORS_RESTRICTEDSET.map((c: any) => c[1]), '#FF6900', '#FCB900', '#7BDCB5', '#00D084', '#8ED1FC', '#0693E3', '#ABB8C3', '#EB144C', '#F78DA7', '#9900EF']} />
        </Stack>
    );
};

function PositionSource({ tracker, trackerId }: { tracker: any, trackerId: string }) {
    const dispatch = useDispatch();
    const telemetrySources = useSelector((state: RootState) => state.telemetry.labels[trackerId].filter((l: any) => l.is_source));

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value !== "") dispatch(
            trackingActions.set_tracker_source({
                plid: trackerId,
                source: event.target.value
            })
        );
    };

    return (
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Position Source:
            </Typography>
            <TextField
                select
                value={tracker.source || ""}
                onChange={onChange}
                variant="outlined"
                size="small"
                SelectProps={{
                    native: true,
                }}
            >
                <option value={""}>{""}</option>
                {
                    telemetrySources.map((l: any) => <option key={l.label} value={l.label}>{l.label}</option>)
                }
            </TextField>
        </Stack>
    );
};

function TrackColor({ tracker, trackerId }: { tracker: any, trackerId: string }) {
    const dispatch = useDispatch();
    const confirm = useConfirm();

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const new_gradient = event.target.value;

        if (new_gradient !== COLOR_TRACK_NONE) {
            confirm({
                confirmationButtonProps: { color: "inherit" },
                cancellationButtonProps: { color: "inherit" },
                description: `Changing track color gradient to ${COLOR_TRACKING_FUNCTIONS[new_gradient].name} may degrade app performances. Please confirm.`
            })
                .then(() => {
                    dispatch(
                        trackingActions.set_tracker_color_track({
                            plid: trackerId,
                            colorTrack: new_gradient
                        })
                    );
                })
                .catch(() => { });
        } else {
            dispatch(
                trackingActions.set_tracker_color_track({
                    plid: trackerId,
                    colorTrack: new_gradient
                })
            );
        };
    };

    return (
        <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Track Color Gradient:
            </Typography>
            <TextField
                select
                value={tracker.colorTrack}
                onChange={onChange}
                variant="outlined"
                size="small"
                SelectProps={{
                    native: true,
                }}
            >
                {
                    Object.entries(COLOR_TRACKING_FUNCTIONS).map(([key, value]) => {
                        return (<option key={key} value={key}>{value.name}</option>);
                    })
                }
            </TextField>
        </Stack>
    );
};

function Tracker(props: any) {
    const dispatch = useDispatch();
    const [elRef, { width }] = useElementSize();
    const { trackerId, tracker, map, expanded, handleChange } = props;
    const isActive = useSelector((state: RootState) => state.replay.isActive);
    const last = useSelector((state: RootState) => lastSelector(state, { id: trackerId, source: tracker.source }));

    const toggleLayer = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.checked
            ? dispatch(trackingActions.show({ plid: trackerId }))
            : dispatch(trackingActions.hide({ plid: trackerId }));
    };

    const onClick = (e: any) => {
        e.stopPropagation();
    };

    return (
        <Box ref={elRef} sx={{ width: '100%' }}>
            <Accordion expanded={expanded === trackerId} onChange={handleChange(trackerId)} TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%' }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="flex-start"
                        >
                            <SvgIcon sx={{ fontSize: '1.2rem' }} viewBox={VEHICLE_KINDS[tracker.kind].icon.viewbox}>
                                {VEHICLE_KINDS[tracker.kind].icon.svg_jsx(tracker.color, tracker.color)}
                            </SvgIcon>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', width: `${width / 3.5}px`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tracker.name}</Typography>
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', maxWidth: `${width / 5}px`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{`(${last?.displayDate
                                ? last.displayDate
                                : "no data"
                                })`}
                            </Typography>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="flex-end"
                        >
                            {!tracker.virtual &&
                                <Typography variant="h6" sx={{ fontSize: '0.9rem', textTransform: "uppercase", color: isActive ? "darkgrey" : tracker.status === "online" ? '#44b700' : tracker.status === "offline" ? "red" : "grey" }}>
                                    {isActive ?
                                        "replay"
                                        :
                                        tracker.status
                                    }
                                </Typography>
                            }
                            <Tooltip title={`${tracker.shown ? "Hide" : "Show"} on map`} placement="right">
                                <Switch
                                    size="small"
                                    checked={tracker.shown}
                                    onClick={onClick}
                                    onChange={toggleLayer}
                                    color="secondary"
                                />
                            </Tooltip>
                        </Stack>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ width: '100%', padding: 1 }}>
                    <Stack direction="column" spacing={0.5} sx={{ width: '100%' }}>
                        <Actions tracker={tracker} trackerId={trackerId} lastPosition={last?.position} map={map} />
                        <TrackLength tracker={tracker} trackerId={trackerId} />
                        <VehicleColor tracker={tracker} trackerId={trackerId} />
                        <PositionSource tracker={tracker} trackerId={trackerId} />
                        <TrackColor tracker={tracker} trackerId={trackerId} />
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default function Control({ map, close }: { map: Map, close: () => void }) {

    const tracking = useSelector((state: RootState) => state.nav.tracking);
    const [expanded, setExpanded] = useState<string | false>(false);

    const handleChange =
        (trackerId: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? trackerId : false);
        };

    return (
        <Stack direction="column" spacing={0}>
            {Object.entries(tracking).map(([trackerId, tracker]) => (
                <Tracker key={trackerId} trackerId={trackerId} tracker={tracker} map={map} expanded={expanded} handleChange={handleChange} />
            ))}
        </Stack>
    );
};